<template>
    <HamletStaticTable
        :columns="columns"
        :items="state.items"
        :empty-mode="state.loading ? 'hidden' : 'message'"
        border-variant="light"
        card-class="forwarded-requests-status mb-0"
        aria-live="polite"
    >
        <template #header>
            <div class="forwarded-requests-status__header">
                <div>
                    <h5 class="mb-25">
                        {{ translate(
                            'Page(ReceivedRequest):Component(RequestHandling):forwarded-requests-title',
                            'Forwarded requests',
                        ) }}
                    </h5>
                    <p class="mb-0 text-less-muted">
                        {{ translate(
                            'Page(ReceivedRequest):Component(RequestHandling):forwarded-requests-description',
                            'Requests created for suppliers while handling this request.',
                        ) }}
                    </p>
                </div>

                <div class="forwarded-requests-status__header-controls">
                    <template v-if="state.error">
                        <span class="text-danger">
                            {{ translate(
                                'Page(ReceivedRequest):Component(RequestHandling):forwarded-requests-load-error',
                                'Could not refresh requests.',
                            ) }}
                        </span>
                        <b-button size="sm" variant="outline-danger" @click="$emit('refresh')">
                            {{ translate('Component(Button):retry', 'Retry') }}
                        </b-button>
                    </template>
                    <b-spinner
                        v-else-if="state.loading"
                        small
                        variant="primary"
                        :label="translate('Component(Text):loading', 'Loading')"
                    />
                    <b-button
                        v-if="showForwardAction"
                        variant="primary"
                        :disabled="forwardDisabled"
                        @click="$emit('forward')"
                    >
                        {{ translate(
                            'Page(ReceivedRequest):Component(RequestHandling):forward-another-request',
                            'Forward another request',
                        ) }}
                    </b-button>
                </div>
            </div>
        </template>

        <template #no-records>
            {{ translate(
                'Page(ReceivedRequest):Component(RequestHandling):no-forwarded-requests',
                'No forwarded requests were found.',
            ) }}
        </template>

        <template #cell-product="{ item }">
            <div class="forwarded-requests-status__product-name">
                {{ item.targetProduct.name }}
            </div>
            <small v-if="item.targetProduct.identifier" class="text-less-muted">
                {{ item.targetProduct.identifier }}
            </small>
        </template>

        <template #cell-started="{ item }">
            <span class="text-less-muted">
                {{ formatDate(item.forwardStartedAt) }}
            </span>
        </template>

        <template #cell-status="{ item }">
            <span
                v-if="item.forwardState === 'pending_request_creation'"
                class="forwarded-requests-status__pending"
            >
                <b-spinner small variant="primary" />
                <span class="text-less-muted">
                    {{ translate(
                        'Page(ReceivedRequest):Component(RequestHandling):creating-request',
                        'Creating request…',
                    ) }}
                </span>
            </span>
            <StatusBadge v-else-if="item.request" type="sent" :status="item.request.status" />
        </template>

        <template #cell-action="{ item }">
            <b-button
                v-if="item.request"
                size="sm"
                variant="outline-primary"
                @click="$emit('open', item.request)"
            >
                {{ translate('Component(Button):open-request', 'Open request') }}
            </b-button>
        </template>
    </HamletStaticTable>
</template>

<script>
import {
    BButton,
    BSpinner,
} from 'bootstrap-vue'
import moment from 'moment'
import HamletStaticTable from '@core/components/common/HamletStaticTableV1.vue'
import StatusBadge from '@/views/request/components/StatusBadge.vue'

export default {
    name: 'ForwardedRequestsStatus',
    components: {
        BButton,
        BSpinner,
        HamletStaticTable,
        StatusBadge,
    },
    props: {
        state: { type: Object, required: true },
        showForwardAction: { type: Boolean, default: false },
        forwardDisabled: { type: Boolean, default: false },
    },
    data() {
        return {
            columns: [
                {
                    key: 'product',
                    label: this.translate('Component(Text):product', 'Product'),
                },
                {
                    key: 'started',
                    label: this.translate('Component(Text):started', 'Started'),
                },
                {
                    key: 'status',
                    label: this.translate('Component(Text):status', 'Status'),
                },
                {
                    key: 'action',
                    label: '',
                    thClass: 'forwarded-requests-status__action-column',
                    tdClass: 'forwarded-requests-status__action-column',
                },
            ],
        }
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        formatDate(value) {
            return value ? moment(value).format('YYYY-MM-DD HH:mm') : ''
        },
    },
}
</script>

<style lang="scss" scoped>
.forwarded-requests-status {
    margin-top: 1rem;
}

.forwarded-requests-status::v-deep .forwarded-requests-status__action-column {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}

.forwarded-requests-status__header {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
}

.forwarded-requests-status__header-controls,
.forwarded-requests-status__pending {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: .75rem;
}

.forwarded-requests-status__product-name {
    color: #2f2c52;
    font-weight: 600;
}

@media (max-width: 767.98px) {
    .forwarded-requests-status__header {
        align-items: flex-start;
        flex-direction: column;
    }
}
</style>
